/**
 * /wins — Contest recap landing.
 *
 * Public read-only page that shows the running vote tally + comment wall +
 * (optional) embedded demo clip. Founder sets the clip URL via
 * POST /superadmin/vote/clip so this page lights up after the pitch.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { SEO } from "@/components/shared/SEO";
import { Trophy, Play, Users, Star, Sparkles, TrendingUp, ExternalLink, Share2 } from "lucide-react";

interface WinsPayload {
  total: number;
  last_hour: number;
  last_24h: number;
  demo_clip_url?: string | null;
  demo_clip_caption?: string | null;
  demo_clip_set_at?: string | null;
  by_role: Array<{ role: string; count: number }>;
  wall: Array<{ vote_id: string; voter_name?: string | null; voter_role?: string | null; comment?: string | null; created_at: string }>;
}

function isVideoUrl(u: string | null | undefined): boolean {
  if (!u) return false;
  return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(u);
}

function isYouTube(u: string): string | null {
  const m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

function isLoom(u: string): string | null {
  const m = u.match(/loom\.com\/(?:share|embed)\/([A-Za-z0-9]+)/);
  return m ? m[1] : null;
}

export default function WinsPage() {
  const q = useQuery<WinsPayload>({
    queryKey: ["wins"],
    queryFn: async () => (await api.get("/vote/wins")).data,
    refetchInterval: 15_000,
  });

  const d = q.data;
  const clipUrl = d?.demo_clip_url || null;

  return (
    <div className="min-h-screen bg-[color:var(--sg-bg)]" data-testid="wins-page">
      <SEO title="Wins · SociBaba" path="/wins"/>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Hero */}
        <div className="rounded-3xl overflow-hidden relative border border-[color:var(--sg-border)] mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-700 via-[color:var(--sb-navy,#0A1128)] to-[color:var(--sb-saffron,#F97316)]"/>
          <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 30%, rgba(16,185,129,0.35), transparent 40%), radial-gradient(circle at 80% 70%, rgba(249,115,22,0.35), transparent 40%)" }}/>
          <div className="relative px-6 sm:px-10 py-10 sm:py-14 text-white">
            <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-white/70 flex items-center gap-2">
              <Trophy className="h-3 w-3" strokeWidth={2.5}/> AI App Builders Contest · Recap
            </div>
            <h1 className="mt-3 font-serif-display text-4xl sm:text-6xl font-bold leading-tight">
              Wins so far.
            </h1>
            <p className="mt-3 text-base sm:text-lg text-white/85 max-w-2xl">
              Every vote judges left for SociBaba, every kind note, and the recorded demo — all in one place.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <StatChip label="Votes" value={d?.total ?? "—"} tone="light"/>
              <StatChip label="Last hour" value={d?.last_hour ?? "—"} tone="ghost"/>
              <StatChip label="Last 24h" value={d?.last_24h ?? "—"} tone="ghost"/>
              <a
                href="/vote"
                data-testid="wins-vote-cta"
                className="inline-flex items-center h-11 px-5 rounded-full bg-white text-[color:var(--sb-navy,#0A1128)] font-bold text-sm hover:bg-white/90"
              >
                Cast your vote →
              </a>
              <a
                data-testid="wins-share-linkedin"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 h-11 px-4 rounded-full bg-[#0A66C2] text-white font-semibold text-sm hover:bg-[#004182]"
              >
                <Share2 className="h-4 w-4"/> Share on LinkedIn
              </a>
            </div>
          </div>
        </div>

        {/* Demo clip */}
        <section data-testid="wins-clip" className="mb-8">
          <SectionHeader icon={Play} label="Recorded demo"/>
          {clipUrl ? (
            <div className="rounded-2xl overflow-hidden border border-[color:var(--sg-border)] bg-black">
              <ClipPlayer url={clipUrl}/>
              {d?.demo_clip_caption && (
                <div className="px-5 py-3 text-sm text-white/80 bg-slate-900">
                  {d.demo_clip_caption}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-8 text-center text-sm text-[color:var(--sg-muted)]">
              <Play className="h-6 w-6 mx-auto mb-2 opacity-60"/>
              The demo clip lands here as soon as the founder uploads it. Refresh in a few minutes.
            </div>
          )}
        </section>

        {/* Comment wall */}
        <section className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
          <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-6" data-testid="wins-wall">
            <SectionHeader icon={Star} label="What voters said"/>
            {(d?.wall?.length ?? 0) === 0 ? (
              <div className="text-sm text-[color:var(--sg-muted)] py-6 text-center">
                No comments yet — be the first at <a className="text-[color:var(--sb-saffron,#F97316)] font-semibold hover:underline" href="/vote">/vote</a>.
              </div>
            ) : (
              <div className="space-y-3">
                {d!.wall.map((it) => (
                  <div key={it.vote_id} className="rounded-xl border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[color:var(--sb-saffron,#F97316)] to-fuchsia-500 text-white flex items-center justify-center text-[11px] font-bold">
                        {(it.voter_name || "A").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[color:var(--sg-fg)]">{it.voter_name || "Anonymous"}</div>
                        {it.voter_role && <div className="text-[10px] uppercase tracking-wide text-[color:var(--sg-muted)]">{it.voter_role}</div>}
                      </div>
                      <div className="ml-auto text-[10px] text-[color:var(--sg-muted)]">{new Date(it.created_at).toLocaleString()}</div>
                    </div>
                    <p className="mt-2 text-sm text-[color:var(--sg-fg)] leading-relaxed">“{it.comment}”</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-6 h-fit" data-testid="wins-roles">
            <SectionHeader icon={Users} label="Who's voting"/>
            {(d?.by_role?.length ?? 0) === 0 ? (
              <div className="text-sm text-[color:var(--sg-muted)] py-4 text-center">No votes yet.</div>
            ) : (
              <div className="space-y-2">
                {d!.by_role.map((r) => {
                  const max = Math.max(1, ...d!.by_role.map((x) => x.count));
                  return (
                    <div key={r.role}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-[color:var(--sg-fg)] capitalize">{r.role}</span>
                        <span className="text-[color:var(--sg-muted)] tabular-nums">{r.count}</span>
                      </div>
                      <div className="mt-1 h-2 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-[color:var(--sb-saffron,#F97316)] to-fuchsia-500" style={{ width: `${(r.count / max) * 100}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <div className="mt-10 text-center text-[11px] text-[color:var(--sg-muted)]">
          <a href="/" className="hover:text-[color:var(--sb-saffron,#F97316)]">← Back to SociBaba</a>
          <span className="mx-2">·</span>
          Live at <span className="font-mono">siba-score-ai.preview.emergentagent.com</span>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, label }: { icon: any; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-bold text-[color:var(--sg-muted)] mb-3">
      <Icon className="h-3 w-3"/> {label}
    </div>
  );
}

function StatChip({ label, value, tone }: { label: string; value: number | string; tone: "light" | "ghost" }) {
  const cls = tone === "light"
    ? "bg-white/95 text-[color:var(--sb-navy,#0A1128)] shadow-lg"
    : "bg-white/15 backdrop-blur-sm text-white border border-white/20";
  return (
    <div className={`rounded-2xl px-5 py-3 ${cls}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${tone === "light" ? "text-[color:var(--sb-saffron,#F97316)]" : "text-white/60"}`}>{label}</div>
      <div className="font-serif-display text-3xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function ClipPlayer({ url }: { url: string }) {
  const yt = isYouTube(url);
  if (yt) {
    return (
      <div className="aspect-video">
        <iframe
          data-testid="wins-yt-embed"
          src={`https://www.youtube.com/embed/${yt}`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  const loom = isLoom(url);
  if (loom) {
    return (
      <div className="aspect-video">
        <iframe
          data-testid="wins-loom-embed"
          src={`https://www.loom.com/embed/${loom}`}
          className="w-full h-full"
          allowFullScreen
        />
      </div>
    );
  }
  if (isVideoUrl(url)) {
    return (
      <video
        data-testid="wins-video"
        controls
        preload="metadata"
        className="w-full aspect-video bg-black"
      >
        <source src={url}/>
        Your browser can't play this file.{" "}
        <a href={url} className="text-[color:var(--sb-saffron,#F97316)] underline">Download</a>
      </video>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid="wins-clip-fallback"
      className="flex items-center justify-center gap-2 aspect-video bg-slate-900 text-white hover:bg-slate-800"
    >
      <ExternalLink className="h-5 w-5"/> Open demo clip
    </a>
  );
}
